import { FC, ReactElement, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { format } from "date-fns";

import { tokens } from "@/tokens";
import { tableCompactSx } from "@/components/table/table-styles";
import TablePagination from "@/components/paginations/table-pagination";
import { tariff_code_placeholder } from "@/constants";
import { numberWithCommas, responsiveFontSizes } from "@/utils";
import { useGetEntrySummariesQuery } from "@/services";
import { useEffectiveClientScope } from "@/hooks";

interface EntriesDetailProps {
  opportunity: PrefOriginOpportunity;
  onBack: () => void;
  dateFrom: string;
  dateTo: string;
  initialManufacturerId?: string;
  manufacturers?: PrefOriginManufacturer[];
}

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  FULLY_CLAIMED: { label: "Fully Claimed", color: "#2E7D32", bg: "#E8F5E9" },
  PARTIALLY_CLAIMED: { label: "Partially Claimed", color: "#ED6C02", bg: "#FFF3E0" },
  NOT_CLAIMED: { label: "Not Claimed", color: "#D32F2F", bg: "#FFEBEE" },
};

type TabValue = "all" | "claimed" | "unclaimed";

const EntriesDetail: FC<EntriesDetailProps> = ({
  opportunity,
  onBack,
  dateFrom,
  dateTo,
  initialManufacturerId,
  manufacturers = [],
}): ReactElement => {
  const { clientId, tenantId, isTDAdmin } = useEffectiveClientScope();

  const [activeTab, setActiveTab] = useState<TabValue>("all");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // MID filter — initialized from manufacturer grouped view if applicable
  const [selectedMid, setSelectedMid] = useState<PrefOriginManufacturer | null>(
    () => {
      if (!initialManufacturerId) return null;
      return manufacturers.find((m) => m.manufacturerID === initialManufacturerId) ?? {
        manufacturerID: initialManufacturerId,
        manufacturerName: initialManufacturerId,
        entryCount: 0,
      };
    }
  );

  // Build filters for the entry summaries query
  const backendFilters = useMemo(() => {
    const f: Record<string, any> = {};

    // Filter by HTS code (schema expects [String])
    f.htsCode = [opportunity.htsNumberFull];

    // Filter by country of origin (schema expects [String])
    f.countryOfOrigin = [opportunity.countryOfOriginCode];

    // Filter by claimed/unclaimed status
    if (activeTab === "claimed") {
      f.spiClaimedStatus = "claimed";
    } else if (activeTab === "unclaimed") {
      f.spiClaimedStatus = "unclaimed";
    }

    // Filter by manufacturer ID
    if (selectedMid) {
      f.manufacturerID = [selectedMid.manufacturerID];
    }

    return f;
  }, [opportunity, activeTab, selectedMid]);

  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetEntrySummariesQuery(
    {
      pageSize,
      pageNo: page,
      dateFrom,
      dateTo,
      sortBy: "entryDateSeconds",
      sortType: -1,
      clientId,
      ...(isTDAdmin && tenantId && { tenantId }),
      filters: backendFilters,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const entries = apiData?.getEntrySummaries?.data ?? [];
  const totalCount = apiData?.getEntrySummaries?.meta?.countDocs ?? 0;

  // Expandable rows
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());
  const toggleEntry = (esn: string) => {
    setExpandedEntries((prev) => {
      const next = new Set(prev);
      if (next.has(esn)) next.delete(esn);
      else next.add(esn);
      return next;
    });
  };

  const statusCfg = statusConfig[opportunity.status] ?? {
    label: opportunity.status,
    color: tokens.text.secondary,
    bg: tokens.bg.hover,
  };

  return (
    <Stack spacing={2.5}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <IconButton onClick={onBack} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
            <Typography
              variant="h5"
              fontWeight={tokens.fontWeight.bold}
              sx={{ fontSize: responsiveFontSizes.lg }}
            >
              {opportunity.htsNumberFull}
            </Typography>
            <Chip
              label={statusCfg.label}
              size="small"
              sx={{
                color: statusCfg.color,
                backgroundColor: statusCfg.bg,
                fontWeight: tokens.fontWeight.semibold,
                fontSize: "11px",
              }}
            />
          </Stack>
          <Typography
            variant="body2"
            sx={{ color: tokens.text.secondary, fontSize: responsiveFontSizes.xs, mt: 0.25 }}
          >
            {opportunity.countryName} ({opportunity.countryOfOriginCode}) &middot; {opportunity.program}
          </Typography>
          {opportunity.htsDescription && (
            <Typography
              variant="body2"
              sx={{ color: tokens.text.muted, fontSize: responsiveFontSizes.xs, mt: 0.25 }}
            >
              {opportunity.htsDescription}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          size="small"
          disabled
          sx={{ whiteSpace: "nowrap", opacity: 0.6 }}
        >
          Create Certificate of Origin (Coming Soon)
        </Button>
      </Stack>

      {/* Summary chips */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap">
        <Chip
          label={`Entries: ${numberWithCommas(opportunity.totalEntries)}`}
          variant="outlined"
          size="small"
        />
        <Chip
          label={`Lines: ${numberWithCommas(opportunity.totalLines)}`}
          variant="outlined"
          size="small"
        />
        <Chip
          label={`Claimed Lines: ${numberWithCommas(opportunity.claimedEntries)}`}
          variant="outlined"
          size="small"
          sx={{ color: "#2E7D32", borderColor: "#2E7D32" }}
        />
        <Chip
          label={`Unclaimed Lines: ${numberWithCommas(opportunity.unclaimedEntries)}`}
          variant="outlined"
          size="small"
          sx={{ color: "#D32F2F", borderColor: "#D32F2F" }}
        />
        <Chip
          label={`Potential Savings: $${numberWithCommas(opportunity.potentialSavings)}`}
          variant="outlined"
          size="small"
          sx={{ color: "#2E7D32", borderColor: "#2E7D32", fontWeight: tokens.fontWeight.bold }}
        />
      </Stack>

      {/* MID filter + Tabs */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => {
            setActiveTab(val as TabValue);
            setPage(1);
          }}
          sx={{ borderBottom: `1px solid ${tokens.border.light}` }}
        >
          <Tab label="All" value="all" />
          <Tab label="Claimed" value="claimed" />
          <Tab label="Unclaimed" value="unclaimed" />
        </Tabs>
        <Autocomplete
          size="small"
          options={manufacturers}
          value={selectedMid}
          onChange={(_, val) => {
            setSelectedMid(val);
            setPage(1);
          }}
          getOptionLabel={(opt) => {
            const label = opt.manufacturerName !== opt.manufacturerID
              ? opt.manufacturerName
              : opt.manufacturerID;
            return `${label}`;
          }}
          isOptionEqualToValue={(opt, val) => opt.manufacturerID === val.manufacturerID}
          renderInput={(params) => (
            <TextField {...params} placeholder="All Manufacturers" />
          )}
          sx={{ minWidth: { xs: 0, sm: 280 }, width: { xs: "100%", sm: "auto" } }}
        />
      </Stack>

      {/* Entries Table */}
      <Box
        sx={{
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.border.default}`,
          bgcolor: tokens.bg.surface,
          overflow: "hidden",
          boxShadow: tokens.shadow.sm,
          ...tableCompactSx,
        }}
      >
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                {["Entry #", "Date", "Lines", "Goods Value", "Duties", "Filer"].map((h) => (
                  <TableCell key={h}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(isLoading || isFetching) ? (
                Array.from({ length: pageSize }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="rounded" height={18} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: "none" }}>
                    <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 6 }}>
                      <img src={tariff_code_placeholder} alt="No data" style={{ width: 120, height: 120, opacity: 0.6 }} />
                      <Typography variant="h6" fontWeight={tokens.fontWeight.semibold} color={tokens.text.primary}>
                        No Entries Found
                      </Typography>
                      <Typography variant="body2" sx={{ color: tokens.text.secondary, maxWidth: 420, textAlign: 'center', lineHeight: 1.6 }}>
                        No entries found for this filter. Try adjusting the tab or date range.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry: any, idx: number) => {
                  const esn = entry.entrySummaryNumber ?? `idx-${idx}`;
                  const isOpen = expandedEntries.has(esn);
                  const lines: any[] = (entry.lines ?? []).filter(
                    (l: any) => l.htsNumberFull === opportunity.htsNumberFull && l.countryOfOriginCode === opportunity.countryOfOriginCode
                  );

                  return (
                    <ExpandableEntryRow
                      key={esn}
                      entry={entry}
                      lines={lines}
                      isExpanded={isOpen}
                      onToggle={() => toggleEntry(esn)}
                    />
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Pagination */}
      {!isLoading && totalCount > 0 && (
        <Box
          sx={{
            borderRadius: tokens.radius.lg,
            border: `1px solid ${tokens.border.default}`,
            overflow: "hidden",
          }}
        >
          <TablePagination
            limit={pageSize}
            page={page}
            total={totalCount}
            handlePageChange={setPage}
          />
        </Box>
      )}
    </Stack>
  );
};

// --- Expandable entry row with nested lines ---

const ExpandableEntryRow: FC<{
  entry: any;
  lines: any[];
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ entry, lines, isExpanded, onToggle }) => (
  <>
    <TableRow
      onClick={onToggle}
      sx={{
        cursor: "pointer",
        backgroundColor: isExpanded ? tokens.bg.hover : tokens.bg.surface,
        transition: "background-color 150ms ease",
        "&:hover": { backgroundColor: tokens.bg.hover },
        "& > td": {
          borderBottom: isExpanded ? "none" : `1px solid ${tokens.border.light}`,
        },
      }}
    >
      <TableCell sx={{ width: 48, p: 1, pl: 1.5 }}>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggle(); }} sx={{ p: 0.5 }}>
          {isExpanded ? (
            <KeyboardArrowDown sx={{ color: tokens.brand.primary, fontSize: 20 }} />
          ) : (
            <KeyboardArrowRight sx={{ color: tokens.text.secondary, fontSize: 20 }} />
          )}
        </IconButton>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={tokens.fontWeight.bold} sx={{ fontSize: tokens.fontSize.base, color: tokens.text.primary }}>
          {entry.entrySummaryNumber ?? "N/A"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary }}>
          {entry.entryDate ? format(new Date(entry.entryDate), "MM/dd/yyyy") : "N/A"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.primary }}>
          {entry.lineCount ?? 0} lines
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.primary }}>
          ${numberWithCommas(entry.totalGoodsValue ?? 0)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.primary }}>
          ${numberWithCommas(entry.totalLineDuties ?? 0)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.primary }}>
          {entry.filerCode || "-"}
        </Typography>
      </TableCell>
    </TableRow>

    {/* Expanded: line-level detail */}
    <TableRow sx={{ ...(!isExpanded && { display: "none" }) }}>
      <TableCell colSpan={7} sx={{ p: 0, border: "none" }}>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ backgroundColor: tokens.bg.hover, px: 3, py: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Line #", "HTS Code", "Country", "Manufacturer", "SPI", "Goods Value", "MFN Duty"].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontSize: tokens.fontSize["2xs"],
                        fontWeight: tokens.fontWeight.semibold,
                        color: tokens.text.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        borderBottom: `1px solid ${tokens.border.light}`,
                        py: 0.75,
                        px: 1.5,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: tokens.text.muted }}>
                      No line data available
                    </TableCell>
                  </TableRow>
                ) : (
                  lines.map((line: any, i: number) => (
                    <TableRow
                      key={`${line.entrySummaryLineNumber ?? i}`}
                      sx={{
                        backgroundColor: tokens.bg.surface,
                        "& > td": { borderBottom: `1px solid ${tokens.border.light}`, py: 0.75, px: 1.5 },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>
                          {line.entrySummaryLineNumber ?? line.entryLine ?? i + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.sm2, fontFamily: "monospace", color: tokens.text.primary }}>
                          {line.htsNumberFull ?? "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>
                          {line.countryOfOriginCode ?? "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.secondary }}>
                          {line.manufacturerName || line.manufacturerID || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: tokens.fontSize.sm2,
                            fontWeight: line.lineSPICode ? tokens.fontWeight.semibold : tokens.fontWeight.normal,
                            color: line.lineSPICode ? "#2E7D32" : tokens.text.muted,
                          }}
                        >
                          {line.lineSPICode || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>
                          ${numberWithCommas(line.lineTariffGoodsValueAmount ?? 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>
                          ${numberWithCommas(line.mfnDuty ?? 0)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  </>
);

export default EntriesDetail;
